import { DataStation } from "./../types/datastation.type";
import { responseHandlerDefault } from "./../utils/handler.util";
import { Pagination } from "./../types/pagination.type";
import axios from "./api";

/**
 * get the data list of a station
 * @param {string} station_id
 * @returns {Promise<DataStation[]>}
 */
export const getData = (station_id: string): Promise<DataStation[]> => {
  return axios
    .get<DataStation[]>(`/datastation/${station_id}`)
    .then((res) => {
      if (res.status === 200) {
        return res.data;
      }
      throw new Error(res.status.toString());
    })
    .catch((res) => {
      throw new Error(res.status);
    });
};

/**
 * get environmental data
 * @param {string} station_id
 * @returns {Promise<DataStation[]>}
 */
export const getDataEnvironmental = (station_id: string): Promise<DataStation[]> => {
  return axios
    .get<DataStation[]>(`/datastation/environmental/${station_id}`)
    .then((res) => {
      if (res.status === 200) {
        return res.data;
      }
      throw new Error(res.status.toString());
    })
    .catch((res) => {
      throw new Error(res.status);
    });
};

/**
 * Get operational historical data with pagination, date range, and value filtering.
 * @param {string} stationId
 * @param {string} magnitudeName
 * @param {number} [page] - Optional page number
 * @param {number} [size] - Optional page size
 * @param {string} [startDate] - Optional start date for filtering
 * @param {string} [endDate] - Optional end date for filtering
 * @param {string} [value] - Optional text value to filter by
 * @returns {Promise<Pagination<DataStation>>}
 */
export const getDataHistoric = (
  stationId: string,
  magnitudeName: string,
  page?: number,
  size?: number,
  startDate?: string,
  endDate?: string,
  value?: string
): Promise<Pagination<DataStation>> => {
  const params: {
    page?: number;
    size?: number;
    startDate?: string;
    endDate?: string;
    value?: string;
  } = {};

  if (page) params.page = page;
  if (size) params.size = size;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (value) params.value = value;

  return axios
    .get(`/datastation/operational-historic/${stationId}/${magnitudeName}`, { params })
    .then((res) => {
      if (res.status === 200) {
        return res.data;
      }
      throw new Error(res.status.toString());
    })
    .catch((error) => {
      throw new Error(error.response?.status || "Unknown error");
    });
};

/**
 * Get distinct measurements for a specific station and magnitude.
 * @param {string} stationId
 * @param {string} magnitudeName
 * @returns {Promise<string[]>}
 */
export const getDistinctMeasurements = (
  stationId: string,
  magnitudeName: string
): Promise<string[]> => {
  return axios
    .get(`/datastation/distinct-measurements/${stationId}/${magnitudeName}`)
    .then((res) => {
      if (res.status === 200) {
        return res.data;
      }
      throw new Error(res.status.toString());
    })
    .catch((error) => {
      throw new Error(error.response?.status || "Unknown error");
    });
};

export const getGraphData = (
  station_id: number,
  parameters: string[],
  from: string,
  until: string
): Promise<DataStation[]> => {
  return axios
    .get<DataStation[]>(
      `/datastation/graph?parameters=${parameters}&from=${from}&until=${until}&stationId=${station_id}`
    )
    .then(responseHandlerDefault);
};
export const getGraphDataDates = (
  station_id: number,
  parameters: string[]
): Promise<DataStation[]> => {
  return axios
    .get<DataStation[]>(`/datastation/graphDates?parameters=${parameters}&stationId=${station_id}`)
    .then(responseHandlerDefault);
};
export const getDateBySamplingPoint = (
  samplingPointid: number,
  parameters: string[],
  from: string,
  until: string
): Promise<DataStation[]> => {
  return axios
    .get<DataStation[]>(
      `/datastation/dateBySamplingPoint?samplingPointId=${samplingPointid}&parameters=${parameters}&from=${from}&until=${until}`
    )
    .then(responseHandlerDefault);
};
// export const getDataSamplingPoint = (
//   samplingPointid: number,
//   parameters: string[],
//   from: string,
//   until: string
// ): Promise<DataStation[]> => {
//   console.log(`[datos] cojiendo staciones`);
//   console.log(
//     `[datos] /datastation/graph?parameters=${parameters}&from=${from}&until=${until}&stationId=${station_id}`
//   );
//   return axios
//     .get<DataStation[]>(
//       `/datastation/graph?parameters=${parameters}&from=${from}&until=${until}&stationId=${station_id}`
//     )
//     .then(responseHandlerDefault);
// };

export const getStationCoordinates = () => {
  return axios.get(`/datastation/coordinates`);
};

export const isTextMeasurement = (measurement: string): boolean => {
  // 1. Permitir números enteros sin separadores
  // 1. Permitir puntos o comas como separador de miles, pero no ambos al mismo tiempo.
  // 2. Si hay separador de miles, solo grupos de 3 dígitos en la parte entera.
  // 3. Usar un separador opuesto para los decimales, si los hubiera.
  // 4. No aceptar espacios.
  const onlyNumbers =
    /(^-?\d+$)|(^-?\d+[.,]\d+$)|(^-?\d{1,3}([.]\d{3})*$)|(^-?\d{1,3}([.]\d{3})*[,]\d+$)|(^-?\d{1,3}([,]\d{3})*$)|(^-?\d{1,3}([,]\d{3})*[.]\d+$)/;
  return !onlyNumbers.test(measurement);
};
